import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  CreateCustomClosingDayApiArg,
  CreateCustomClosingDayApiResponse,
  CreateCustomClosingDayApiResponseSchema,
  CreateCustomClosingDayRequestBodySchema,
  DeleteCustomClosingDayApiArg,
  DeleteCustomClosingDayApiArgSchema,
  DeleteCustomClosingDayApiResponse,
  DeleteCustomClosingDayApiResponseSchema,
  GetClosingDayApiArg,
  GetClosingDayApiArgSchema,
  GetClosingDayApiResponse,
  GetClosingDayApiResponseSchema,
  SyncPublicClosingDaysApiArg,
  SyncPublicClosingDaysApiResponse,
  SyncPublicClosingDaysApiResponseSchema,
  SyncPublicClosingDaysRequestBodySchema,
  UpdateCustomClosingDayApiArg,
  UpdateCustomClosingDayApiResponse,
  UpdateCustomClosingDayApiResponseSchema,
  UpdateCustomClosingDayRequestBodySchema,
  UpdatePublicClosingDayApiArg,
  UpdatePublicClosingDayApiResponse,
  UpdatePublicClosingDayApiResponseSchema,
  UpdatePublicClosingDayRequestBodySchema,
} from './types';

export const closingDayApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getClosingDay: build.query<GetClosingDayApiResponse, GetClosingDayApiArg>({
      query: ({closingDayId}) => ({
        url: `/dms/v1/closing-day/${closingDayId}`,
      }),
      providesTags: (result, error, {closingDayId}) => [{type: 'ClosingDay', id: closingDayId}],
      extraOptions: {
        requestSchema: GetClosingDayApiArgSchema,
        responseSchema: GetClosingDayApiResponseSchema,
      },
    }),
    createCustomClosingDay: build.mutation<
      CreateCustomClosingDayApiResponse,
      CreateCustomClosingDayApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-closing-day`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      extraOptions: {
        requestSchema: CreateCustomClosingDayRequestBodySchema,
        responseSchema: CreateCustomClosingDayApiResponseSchema,
      },
    }),
    updateCustomClosingDay: build.mutation<
      UpdateCustomClosingDayApiResponse,
      UpdateCustomClosingDayApiArg
    >({
      query: (queryArg) => {
        const {closingDayId, ...body} = queryArg;
        return {
          url: `/dms/v1/custom-closing-day/${closingDayId}`,
          method: 'PUT',
          body,
          queryArg,
        };
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateCustomClosingDayApiArg) => [
        {type: 'ClosingDay', id: queryArg.closingDayId},
      ]),
      extraOptions: {
        requestSchema: UpdateCustomClosingDayRequestBodySchema,
        responseSchema: UpdateCustomClosingDayApiResponseSchema,
      },
    }),
    deleteCustomClosingDay: build.mutation<
      DeleteCustomClosingDayApiResponse,
      DeleteCustomClosingDayApiArg
    >({
      query: ({closingDayId}) => ({
        url: `/dms/v1/custom-closing-day/${closingDayId}`,
        method: 'DELETE',
      }),
      extraOptions: {
        requestSchema: DeleteCustomClosingDayApiArgSchema,
        responseSchema: DeleteCustomClosingDayApiResponseSchema,
      },
    }),
    syncPublicClosingDays: build.mutation<
      SyncPublicClosingDaysApiResponse,
      SyncPublicClosingDaysApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/public-closing-days/sync`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      extraOptions: {
        requestSchema: SyncPublicClosingDaysRequestBodySchema,
        responseSchema: SyncPublicClosingDaysApiResponseSchema,
      },
    }),
    updatePublicClosingDay: build.mutation<
      UpdatePublicClosingDayApiResponse,
      UpdatePublicClosingDayApiArg
    >({
      query: (queryArg) => {
        const {closingDayId, ...body} = queryArg;
        return {
          url: `/dms/v1/public-closing-day/${closingDayId}`,
          method: 'PUT',
          body,
          queryArg,
        };
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdatePublicClosingDayApiArg) => [
        {type: 'ClosingDay', id: queryArg.closingDayId},
      ]),
      extraOptions: {
        requestSchema: UpdatePublicClosingDayRequestBodySchema,
        responseSchema: UpdatePublicClosingDayApiResponseSchema,
      },
    }),
  }),
});

export const {
  useGetClosingDayQuery,
  useCreateCustomClosingDayMutation,
  useUpdateCustomClosingDayMutation,
  useDeleteCustomClosingDayMutation,
  useSyncPublicClosingDaysMutation,
  useUpdatePublicClosingDayMutation,
} = closingDayApi;
